/**
 * Web Audio 封装：BGM 循环（交叉淡入淡出）+ SFX 多通道限流。
 * BGM 音频缺失时静默跳过，不报错。
 */
import { Assets } from './assets.js';

const BGM_FADE_IN = 0.5;   // 秒
const BGM_FADE_OUT = 0.5;  // 秒
const SFX_DEBOUNCE = 30;   // 同 key 去抖毫秒
const SFX_MAX_PER_KEY = 8; // 同 key 最大并发

export const AudioSys = {
  /** @type {AudioContext|null} */
  ctx: null,
  masterGain: null,
  bgmGain: null,
  sfxGain: null,
  muted: false,
  _bgmSource: null,
  _bgmKey: null,
  /** @type {Map<string, number>} key → 上次播放时间戳(ms) */
  _sfxLastPlay: new Map(),
  /** @type {Map<string, number>} key → 当前并发数 */
  _sfxPlaying: new Map(),

  /**
   * 确保 AudioContext 已创建并 resume。
   * 须在用户手势（click/keydown）中首次调用。
   * @returns {AudioContext|null}
   */
  ensureCtx() {
    if (this.ctx) {
      // iOS Safari 系统音频中断后进 'interrupted' 而非 'suspended'——
      // 只判 suspended 会永不恢复，SFX 计数只增不还最终永久静默（历史 bug）
      if (this.ctx.state !== 'running') this.ctx.resume().catch(() => {});
      return this.ctx;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    this.ctx = new AC();
    this.masterGain = this.ctx.createGain();
    this.masterGain.connect(this.ctx.destination);
    this.bgmGain = this.ctx.createGain();
    this.bgmGain.connect(this.masterGain);
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.connect(this.masterGain);
    this.bgmGain.gain.value = this._loadVol('kimi-vol-bgm', 1);
    this.sfxGain.gain.value = this._loadVol('kimi-vol-sfx', 1);
    return this.ctx;
  },

  /** 读持久化音量（0..1，localStorage，无痕兜底 def）。 */
  _loadVol(key, def) {
    try {
      const v = parseFloat(globalThis.localStorage?.getItem(key));
      return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : def;
    } catch (e) {
      return def;
    }
  },

  /** 设 BGM 音量 0..1 并持久化（内存为准，localStorage 仅副本：写入失败环境调节"失灵"的历史 bug）。 */
  setBgmVolume(v) {
    const vol = Math.max(0, Math.min(1, v));
    this._bgmVol = vol;
    if (this.bgmGain) this.bgmGain.gain.value = vol;
    try {
      globalThis.localStorage?.setItem('kimi-vol-bgm', String(vol));
    } catch (e) { /* 忽略 */ }
  },

  /** 设 SFX 音量 0..1 并持久化（内存为准）。 */
  setSfxVolume(v) {
    const vol = Math.max(0, Math.min(1, v));
    this._sfxVol = vol;
    if (this.sfxGain) this.sfxGain.gain.value = vol;
    try {
      globalThis.localStorage?.setItem('kimi-vol-sfx', String(vol));
    } catch (e) { /* 忽略 */ }
  },

  /** 当前 BGM/SFX 音量（options 界面读；惰性从存储播种到内存一次）。 */
  getBgmVolume() {
    if (this._bgmVol === undefined) this._bgmVol = this._loadVol('kimi-vol-bgm', 1);
    return this._bgmVol;
  },
  getSfxVolume() {
    if (this._sfxVol === undefined) this._sfxVol = this._loadVol('kimi-vol-sfx', 1);
    return this._sfxVol;
  },

  /**
   * 播放 BGM（默认循环）。切换时旧曲淡出、新曲淡入。
   * 资源不存在时静默返回。
   * @param {string} key
   * @param {{loop?: boolean, volume?: number}} [opts]
   */
  playBgm(key, { loop = true, volume = 1 } = {}) {
    const ctx = this.ensureCtx();
    if (!ctx) return;
    if (this._bgmKey === key) return;
    const buffer = Assets.audio(key);
    if (!buffer) { // 无音频文件：停掉旧曲再静默（否则旧曲在结算等场景"放错曲"继续循环）
      this.stopBgm();
      return;
    }
    this.stopBgm();
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = loop;
    const gain = ctx.createGain();
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + BGM_FADE_IN);
    src.connect(gain);
    gain.connect(this.bgmGain);
    src.start();
    this._bgmSource = src;
    this._bgmGainNode = gain;
    this._bgmKey = key;
  },

  /** 停止当前 BGM（淡出）。 */
  stopBgm() {
    if (!this._bgmSource || !this.ctx) return;
    const src = this._bgmSource;
    const gain = this._bgmGainNode;
    const now = this.ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + BGM_FADE_OUT);
    try {
      src.stop(now + BGM_FADE_OUT);
    } catch (e) { /* 已停止 */ }
    this._bgmSource = null;
    this._bgmGainNode = null;
    this._bgmKey = null;
  },

  /**
   * 播放音效。同 key 30ms 内去抖，同 key 最多 8 个并发。
   * @param {string} key
   * @param {{volume?: number, rate?: number}} [opts]
   */
  playSfx(key, { volume = 1, rate = 1 } = {}) {
    const ctx = this.ensureCtx();
    if (!ctx) return;
    const clip = Assets.audio(key);
    if (!clip) return;
    const now = performance.now();
    const last = this._sfxLastPlay.get(key) || 0;
    if (now - last < SFX_DEBOUNCE) return;
    const playing = this._sfxPlaying.get(key) || 0;
    if (playing >= SFX_MAX_PER_KEY) return;
    this._sfxLastPlay.set(key, now);
    this._sfxPlaying.set(key, playing + 1);
    const src = ctx.createBufferSource();
    const isSprite = !!clip.__sprite; // 合并 sprite 分段（tools/build.mjs 产物，main.js 装配）
    src.buffer = isSprite ? clip.buf : clip;
    src.playbackRate.value = rate;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain);
    gain.connect(this.sfxGain);
    src.onended = () => {
      this._sfxPlaying.set(key, (this._sfxPlaying.get(key) || 1) - 1);
    };
    if (isSprite) src.start(0, clip.offset, clip.duration);
    else src.start();
  },

  /**
   * 静音开关（总线增益置 0/1）。
   * @param {boolean} m
   */
  setMuted(m) {
    this.muted = m;
    if (this.masterGain) {
      this.masterGain.gain.value = m ? 0 : 1;
    }
  },
};
